
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type GameProgress = Database['public']['Tables']['game_progress']['Row'];
type GameProgressInsert = Database['public']['Tables']['game_progress']['Insert'];
type GameProgressUpdate = Database['public']['Tables']['game_progress']['Update'];
type GameResult = Database['public']['Tables']['game_results']['Insert'];
type UnlockedFact = Database['public']['Tables']['unlocked_facts']['Insert'];

export interface GameData {
  bestScore: number;
  totalGamesPlayed: number;
  currentStreak: number;
  lastPlayedDate: string;
  unlockedFacts: string[];
  unlockedLevels: number[];
  completedGrids: Record<string, boolean>;
  dailyChallengeCompleted: boolean;
}

/**
 * Get or create user ID from email with enhanced debugging
 */
async function getOrCreateUserId(email: string, userData?: any): Promise<string | null> {
  try {
    console.log('🎮 Supabase - getOrCreateUserId called with email:', email);
    
    // First, try to get existing user
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('🎮 Supabase - Error checking user:', error);
      return null;
    }

    if (data?.id) {
      console.log('🎮 Supabase - Found existing user with ID:', data.id);
      return data.id;
    }

    // User doesn't exist, create new one
    console.log('🎮 Supabase - Creating new user for email:', email);
    const insertData = {
      email: email,
      name: userData?.name || null,
      phone_number: userData?.phoneNumber || null
    };
    console.log('🎮 Supabase - User insert data:', insertData);

    const { data: newUser, error: createError } = await supabase
      .from('users')
      .insert(insertData)
      .select('id')
      .single();

    if (createError) {
      console.error('🎮 Supabase - Error creating user:', createError);
      return null;
    }

    console.log('🎮 Supabase - Created new user with ID:', newUser.id);
    return newUser.id;
  } catch (error) {
    console.error('🎮 Supabase - Error in getOrCreateUserId:', error);
    return null;
  }
}

/**
 * Load game progress from Supabase with enhanced debugging
 */
export async function loadGameProgressFromSupabase(email: string, userData?: any): Promise<GameData | null> {
  try {
    console.log('🎮 Supabase - loadGameProgressFromSupabase called for:', email);
    
    const userId = await getOrCreateUserId(email, userData);
    if (!userId) {
      console.log('🎮 Supabase - Could not get or create user for email:', email);
      return null;
    }

    console.log('🎮 Supabase - Loading game progress for user ID:', userId);

    // Get game progress
    const { data: progress, error: progressError } = await supabase
      .from('game_progress')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (progressError) {
      console.error('🎮 Supabase - Error loading game progress:', progressError);
      return null;
    }

    console.log('🎮 Supabase - Game progress query result:', progress);

    // Get unlocked facts
    const { data: facts, error: factsError } = await supabase
      .from('unlocked_facts')
      .select('fact_key')
      .eq('user_id', userId);

    if (factsError) {
      console.error('🎮 Supabase - Error loading unlocked facts:', factsError);
    }

    console.log('🎮 Supabase - Unlocked facts:', facts);

    if (!progress) {
      console.log('🎮 Supabase - No game progress found for user');
      return null;
    }

    const gameData = {
      bestScore: progress.best_score,
      totalGamesPlayed: progress.total_games_played,
      currentStreak: progress.current_streak,
      lastPlayedDate: progress.last_played_date || '',
      unlockedFacts: facts?.map(f => f.fact_key) || [],
      unlockedLevels: progress.unlocked_levels || [1],
      completedGrids: (progress.completed_grids as Record<string, boolean>) || {},
      dailyChallengeCompleted: progress.daily_challenge_completed
    };

    console.log('🎮 Supabase - Loaded game data:', gameData);
    return gameData;
  } catch (error) {
    console.error('🎮 Supabase - Error in loadGameProgressFromSupabase:', error);
    return null;
  }
}

/**
 * Save game progress to Supabase with enhanced debugging and error handling
 */
export async function saveGameProgressToSupabase(email: string, gameData: GameData, userData?: any): Promise<boolean> {
  try {
    console.log('🎮 Supabase - saveGameProgressToSupabase called for:', email);
    console.log('🎮 Supabase - Game data to save:', gameData);
    
    const userId = await getOrCreateUserId(email, userData);
    if (!userId) {
      console.error('🎮 Supabase - No user ID found for saving game progress');
      return false;
    }

    console.log('🎮 Supabase - Saving game progress for user ID:', userId);

    const progressData: GameProgressUpdate = {
      best_score: gameData.bestScore,
      total_games_played: gameData.totalGamesPlayed,
      current_streak: gameData.currentStreak,
      last_played_date: gameData.lastPlayedDate || null,
      unlocked_levels: gameData.unlockedLevels,
      completed_grids: gameData.completedGrids,
      daily_challenge_completed: gameData.dailyChallengeCompleted,
      updated_at: new Date().toISOString()
    };

    console.log('🎮 Supabase - Progress data prepared:', progressData);

    // Check if user already has game progress
    const { data: existingProgress, error: checkError } = await supabase
      .from('game_progress')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('🎮 Supabase - Error checking existing progress:', checkError);
      return false;
    }

    console.log('🎮 Supabase - Existing progress check result:', existingProgress);

    let result;
    if (existingProgress) {
      // Update existing progress
      console.log('🎮 Supabase - Updating existing game progress');
      result = await supabase
        .from('game_progress')
        .update(progressData)
        .eq('user_id', userId);
    } else {
      // Insert new progress
      console.log('🎮 Supabase - Inserting new game progress');
      result = await supabase
        .from('game_progress')
        .insert({
          user_id: userId,
          ...progressData
        });
    }

    const { error: progressError } = result;

    if (progressError) {
      console.error('🎮 Supabase - Error saving game progress:', progressError);
      console.error('🎮 Supabase - Error details:', {
        code: progressError.code,
        message: progressError.message,
        details: progressError.details,
        hint: progressError.hint
      });
      return false;
    }

    console.log('🎮 Supabase - Game progress saved successfully');
    return true;
  } catch (error) {
    console.error('🎮 Supabase - Error in saveGameProgressToSupabase:', error);
    return false;
  }
}

/**
 * Save a game result to Supabase with enhanced debugging
 */
export async function saveGameResultToSupabase(
  email: string,
  score: number,
  moves: number,
  timeInSeconds: number,
  levelId: number,
  gridId: number,
  isNewBest: boolean,
  unlockedFact?: string,
  userData?: any
): Promise<boolean> {
  try {
    console.log('🎮 Supabase - saveGameResultToSupabase called for:', email);
    console.log('🎮 Supabase - Game result data:', { score, moves, timeInSeconds, levelId, gridId, isNewBest, unlockedFact });
    
    const userId = await getOrCreateUserId(email, userData);
    if (!userId) {
      console.error('🎮 Supabase - No user ID found for saving game result');
      return false;
    }

    const gameResult: GameResult = {
      user_id: userId,
      score,
      moves,
      time_in_seconds: timeInSeconds,
      level_id: levelId,
      grid_id: gridId,
      is_new_best: isNewBest,
      unlocked_fact: unlockedFact || null
    };

    console.log('🎮 Supabase - Prepared game result:', gameResult);

    const { error } = await supabase
      .from('game_results')
      .insert(gameResult);

    if (error) {
      console.error('🎮 Supabase - Error saving game result:', error);
      console.error('🎮 Supabase - Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      return false;
    }

    console.log('🎮 Supabase - Game result saved successfully');
    return true;
  } catch (error) {
    console.error('🎮 Supabase - Error in saveGameResultToSupabase:', error);
    return false;
  }
}

/**
 * Save an unlocked fact to Supabase with enhanced debugging
 */
export async function saveUnlockedFactToSupabase(email: string, factKey: string, userData?: any): Promise<boolean> {
  try {
    console.log('🎮 Supabase - saveUnlockedFactToSupabase called for:', email, 'fact:', factKey);
    
    const userId = await getOrCreateUserId(email, userData);
    if (!userId) {
      console.error('🎮 Supabase - No user ID found for saving unlocked fact');
      return false;
    }

    const factData = {
      user_id: userId,
      fact_key: factKey
    };

    console.log('🎮 Supabase - Prepared fact data:', factData);

    const { error } = await supabase
      .from('unlocked_facts')
      .upsert(factData);

    if (error) {
      console.error('🎮 Supabase - Error saving unlocked fact:', error);
      console.error('🎮 Supabase - Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      return false;
    }

    console.log('🎮 Supabase - Unlocked fact saved successfully');
    return true;
  } catch (error) {
    console.error('🎮 Supabase - Error in saveUnlockedFactToSupabase:', error);
    return false;
  }
}
